import React from "react";
import ImageUploader from "@components/upload/ImageUploader";
import {
  useCategoriesQuery,
  useProductSpecialsQuery,
  useCreateProductSpecialMutation,
  useUpdateProductSpecialMutation,
  useDeleteProductSpecialMutation,
} from "@queries/catalog";

// Descriptions are stored as HTML (rendered as HTML on the app, like the old
// Summernote) but edited in a plain <textarea>. Decode the stored HTML into
// clean editable text on load (incl. double-encoded entities like &amp;nbsp;),
// and convert newlines back to <br> on save so it stays organized on the app.
const htmlToText = (input) => {
  let s = String(input ?? "");
  if (!s) return "";
  const ta = document.createElement("textarea");
  const decode = (str) => { ta.innerHTML = str; return ta.value; };
  for (let i = 0; i < 3; i++) { const d = decode(s); if (d === s) break; s = d; }
  s = s
    .replace(/<\/(p|div|li|h[1-6])\s*>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "");
  return s.replace(/ /g, " ").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
};
const textToHtml = (s) => String(s ?? "").replace(/\r\n?|\n/g, "<br>");

function Tabs({ value, onChange, items, size = "md" }) {
  return (
    <div className={`oc-tabs ${size === "sm" ? "small" : ""}`}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          className={`oc-tab ${value === it.value ? "active" : ""}`}
          onClick={() => onChange(it.value)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function CategoryMultiSelect({ value, onChange }) {
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const pageSize = 50;

  const { data, isLoading, isError, error, isFetching } = useCategoriesQuery(
    search ? { name: search } : {},
    page,
    pageSize
  );

  const selected = value || [];
  const categories = data?.data || [];

  const allCats = React.useMemo(() => {
    const out = [];
    const walk = (nodes, prefix = "") => {
      if (!nodes) return;
      for (const n of nodes) {
        const label = prefix ? `${prefix} / ${n.name}` : n.name;
        out.push({ id: n.id, name: label });
        if (n.children && n.children.length) {
          walk(n.children, label);
        }
      }
    };
    walk(categories);
    return out;
  }, [categories]);

  const toggle = (id) => {
    const set = new Set(selected);
    if (set.has(id)) {
      set.delete(id);
    } else {
      set.add(id);
    }
    onChange(Array.from(set));
  };

  return (
    <div className="oc-cat-multi">
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <input
          className="oc-input"
          placeholder="Search categories..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {isLoading || isFetching ? (
        <div style={{ fontSize: 13 }}>Loading categories…</div>
      ) : isError ? (
        <div style={{ color: "#c00", fontSize: 13 }}>
          Error loading categories: {error?.message || "Unknown error"}
        </div>
      ) : allCats.length === 0 ? (
        <div style={{ fontSize: 13 }}>No categories found.</div>
      ) : (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            maxHeight: 220,
            overflow: "auto",
            padding: 6
          }}
        >
          {allCats.map((c) => {
            const checked = selected.includes(c.id);
            return (
              <label
                key={c.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  padding: "2px 4px"
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(c.id)}
                />
                <span>{c.name}</span>
              </label>
            );
          })}
        </div>
      )}

      {data?.meta && data.meta.total_roots > pageSize && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 6
          }}
        >
          <button
            type="button"
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="oc-btn ghost"
            onClick={() => setPage((p) => p + 1)}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}

const EMPTY_SPECIAL = { priority: 0, price: "", date_start: "", date_end: "" };

function ProductSpecialsTab({ productId }) {
  const { data, isLoading } = useProductSpecialsQuery(productId);
  const createMut = useCreateProductSpecialMutation();
  const updateMut = useUpdateProductSpecialMutation();
  const deleteMut = useDeleteProductSpecialMutation();

  const specials = data?.data ?? (Array.isArray(data) ? data : []);

  const [editing, setEditing] = React.useState(null);
  const [adding, setAdding] = React.useState(null);

  const startAdd = () => setAdding({ ...EMPTY_SPECIAL });
  const cancelAdd = () => setAdding(null);
  const cancelEdit = () => setEditing(null);

  const saveNew = async () => {
    if (!adding) return;
    await createMut.mutateAsync({
      product_id: productId,
      priority: Number(adding.priority || 0),
      price: Number(adding.price || 0),
      date_start: adding.date_start || null,
      date_end: adding.date_end || null,
    });
    setAdding(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const id = editing._id;
    await updateMut.mutateAsync({
      id,
      payload: {
        product_id: productId,
        priority: Number(editing.priority || 0),
        price: Number(editing.price || 0),
        date_start: editing.date_start || null,
        date_end: editing.date_end || null,
      },
    });
    setEditing(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this special price?")) return;
    await deleteMut.mutateAsync({ id });
  };

  if (isLoading) return <div style={{ padding: 12 }}>Loading specials…</div>;

  return (
    <div>
      <div className="table-wrap" style={{ overflowX: "auto" }}>
        <table className="notes-table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>Priority</th>
              <th>Price</th>
              <th>Date Start</th>
              <th>Date End</th>
              <th style={{ width: 100 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {specials.map((s) => {
              const id = s.product_special_id ?? s.id;
              if (editing && editing._id === id) {
                return (
                  <tr key={id}>
                    <td><input className="oc-input" type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} /></td>
                    <td><input className="oc-input" type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></td>
                    <td><input className="oc-input" type="date" value={editing.date_start || ""} onChange={(e) => setEditing({ ...editing, date_start: e.target.value })} /></td>
                    <td><input className="oc-input" type="date" value={editing.date_end || ""} onChange={(e) => setEditing({ ...editing, date_end: e.target.value })} /></td>
                    <td>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button type="button" className="oc-btn" onClick={saveEdit} disabled={updateMut.isPending}>Save</button>
                        <button type="button" className="oc-btn ghost" onClick={cancelEdit}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={id}>
                  <td>{s.priority}</td>
                  <td>{s.price != null && s.price !== "" ? Number(s.price).toFixed(2) : ""}</td>
                  <td>{s.date_start || "—"}</td>
                  <td>{s.date_end || "—"}</td>
                  <td>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button type="button" className="oc-btn ghost" onClick={() => setEditing({ _id: id, priority: s.priority ?? 0, price: s.price ?? "", date_start: s.date_start ?? "", date_end: s.date_end ?? "" })}>Edit</button>
                      <button type="button" className="oc-btn ghost" style={{ color: "#c00" }} onClick={() => remove(id)} disabled={deleteMut.isPending}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {adding && (
              <tr>
                <td><input className="oc-input" type="number" value={adding.priority} onChange={(e) => setAdding({ ...adding, priority: e.target.value })} /></td>
                <td><input className="oc-input" type="number" step="0.01" value={adding.price} onChange={(e) => setAdding({ ...adding, price: e.target.value })} /></td>
                <td><input className="oc-input" type="date" value={adding.date_start} onChange={(e) => setAdding({ ...adding, date_start: e.target.value })} /></td>
                <td><input className="oc-input" type="date" value={adding.date_end} onChange={(e) => setAdding({ ...adding, date_end: e.target.value })} /></td>
                <td>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button type="button" className="oc-btn" onClick={saveNew} disabled={createMut.isPending}>Save</button>
                    <button type="button" className="oc-btn ghost" onClick={cancelAdd}>Cancel</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!adding && (
        <div style={{ marginTop: 8, textAlign: "right" }}>
          <button type="button" className="oc-btn" onClick={startAdd}>+ Add Special Price</button>
        </div>
      )}
    </div>
  );
}

export default function ProductForm({
  initial = {},
  onSubmit,
  onCancel,
  errors = {},
  generalError = ""
}) {
  const [tab, setTab] = React.useState("general");
  const [lang, setLang] = React.useState("ar");

  const [form, setForm] = React.useState(() => {
    const ar = initial.descriptions?.ar || {};
    const en = initial.descriptions?.en || {};

    return {
      id: initial.id ?? null,
      model: initial.model ?? "",
      sku: initial.sku ?? "",
      upc: initial.upc ?? "",
      location: initial.location ?? "",
      image: initial.image ?? "",
      price: Number(initial.price ?? 0),
      quantity: Number(initial.quantity ?? 0),
      minimum: Number(initial.minimum ?? 1),
      sort_order: Number(initial.sort_order ?? 0),
      status: Number(initial.status ?? 1),
      shipping: Number(initial.shipping ?? 1),
      subtract: Number(initial.subtract ?? 1),
      weight: Number(initial.weight ?? 0),
      length: Number(initial.length ?? 0),
      width: Number(initial.width ?? 0),
      height: Number(initial.height ?? 0),
      date_available: initial.date_available ?? "",
      pieces: Number(initial.pieces ?? 0),
      item_count: Number(initial.item_count ?? 0),

      is_flagged: Number(initial.is_flagged ?? 0),
      is_sewing_flagged: Number(initial.is_sewing_flagged ?? 0),

      delivery_withen:
        initial.delivery_withen != null ? String(initial.delivery_withen) : "",
      category_ids: Array.isArray(initial.category_ids)
        ? initial.category_ids
        : [],
      descriptions: {
        ar: {
          language_id: ar.language_id ?? 2,
          name: ar.name ?? "",
          description: ar.description ?? "",
          tag: ar.tag ?? "",
          meta_title: ar.meta_title ?? "",
          meta_description: ar.meta_description ?? "",
          meta_keyword: ar.meta_keyword ?? ""
        },
        en: {
          language_id: en.language_id ?? 1,
          name: en.name ?? "",
          description: en.description ?? "",
          tag: en.tag ?? "",
          meta_title: en.meta_title ?? "",
          meta_description: en.meta_description ?? "",
          meta_keyword: en.meta_keyword ?? ""
        }
      }
    };
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDesc = (loc, k, v) =>
    setForm((f) => ({
      ...f,
      descriptions: {
        ...f.descriptions,
        [loc]: { ...f.descriptions[loc], [k]: v }
      }
    }));

  const getError = (key) => {
    if (!errors) return "";
    const val = errors[key];
    if (!val) return "";
    if (Array.isArray(val)) return val[0];
    return String(val);
  };

  const submit = (e) => {
    e?.preventDefault?.();
    onSubmit?.(form);
  };

  const D = form.descriptions[lang];

  const nameKey =
    lang === "ar" ? "descriptions.ar.name" : "descriptions.en.name";
  const descKey =
    lang === "ar"
      ? "descriptions.ar.description"
      : "descriptions.en.description";
  const tagKey = lang === "ar" ? "descriptions.ar.tag" : "descriptions.en.tag";

  return (
    <form onSubmit={submit}>
      {generalError && (
        <div className="oc-form-error-banner">{generalError}</div>
      )}

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "general", label: "General" },
          { value: "data", label: "Data" },
          ...(form.id ? [{ value: "special", label: "Special" }] : []),
        ]}
      />

      {tab === "general" && (
        <div className="oc-tab-pane">
          <Tabs
            value={lang}
            onChange={setLang}
            items={[
              { value: "ar", label: "Arabic" },
              { value: "en", label: "English" }
            ]}
            size="sm"
          />

          <div className="oc-f-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="oc-field">
              <label>
                {lang === "ar"
                  ? "Product Name (Arabic)"
                  : "Product Name (English)"}
              </label>
              {(() => {
                const err = getError(nameKey);
                return (
                  <>
                    <input
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={D.name}
                      onChange={(e) => setDesc(lang, "name", e.target.value)}
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>{lang === "ar" ? "Tags (Arabic)" : "Tags (English)"}</label>
              {(() => {
                const err = getError(tagKey);
                return (
                  <>
                    <input
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={D.tag}
                      onChange={(e) => setDesc(lang, "tag", e.target.value)}
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field" style={{ gridColumn: "1 / span 2" }}>
              <label>
                {lang === "ar"
                  ? "Description (Arabic)"
                  : "Description (English)"}
              </label>
              {(() => {
                const err = getError(descKey);
                return (
                  <>
                    <textarea
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      rows={8}
                      value={D.description}
                      onChange={(e) =>
                        setDesc(lang, "description", e.target.value)
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {tab === "data" && (
        <div className="oc-tab-pane">
          <div
            className="oc-f-grid"
            style={{ gridTemplateColumns: "1fr 1fr 1fr" }}
          >
            <div className="oc-field">
              <label>Model</label>
              {(() => {
                const err = getError("model");
                return (
                  <>
                    <input
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.model}
                      onChange={(e) => setField("model", e.target.value)}
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>SKU</label>
              {(() => {
                const err = getError("sku");
                return (
                  <>
                    <input
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.sku}
                      onChange={(e) => setField("sku", e.target.value)}
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>UPC</label>
              {(() => {
                const err = getError("upc");
                return (
                  <>
                    <input
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.upc}
                      onChange={(e) => setField("upc", e.target.value)}
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Image</label>
              {(() => {
                const err = getError("image");
                return (
                  <>
                    <div className="oc-image-input-row">
                      <input
                        className={"oc-input" + (err ? " oc-input-error" : "")}
                        value={form.image}
                        placeholder="img_xxx.png (path)"
                        onChange={(e) => setField("image", e.target.value)}
                      />
                      <ImageUploader
                        value={form.image}
                        onChange={(newPath) => {
                          if (newPath) setField("image", newPath);
                        }}
                      />
                    </div>
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Price</label>
              {(() => {
                const err = getError("price");
                return (
                  <>
                    <input
                      type="number"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.price}
                      onChange={(e) =>
                        setField("price", Number(e.target.value || 0))
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Quantity</label>
              {(() => {
                const err = getError("quantity");
                return (
                  <>
                    <input
                      type="number"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.quantity}
                      onChange={(e) =>
                        setField("quantity", Number(e.target.value || 0))
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Minimum Quantity</label>
              {(() => {
                const err = getError("minimum");
                return (
                  <>
                    <input
                      type="number"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.minimum}
                      onChange={(e) =>
                        setField("minimum", Number(e.target.value || 1))
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Sort Order</label>
              {(() => {
                const err = getError("sort_order");
                return (
                  <>
                    <input
                      type="number"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.sort_order}
                      onChange={(e) =>
                        setField("sort_order", Number(e.target.value || 0))
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Status</label>
              {(() => {
                const err = getError("status");
                return (
                  <>
                    <select
                      className={"oc-select" + (err ? " oc-input-error" : "")}
                      value={form.status}
                      onChange={(e) =>
                        setField("status", Number(e.target.value))
                      }
                    >
                      <option value={1}>Enabled</option>
                      <option value={0}>Disabled</option>
                    </select>
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Shipping Required</label>
              {(() => {
                const err = getError("shipping");
                return (
                  <>
                    <select
                      className={"oc-select" + (err ? " oc-input-error" : "")}
                      value={form.shipping}
                      onChange={(e) =>
                        setField("shipping", Number(e.target.value))
                      }
                    >
                      <option value={1}>Yes</option>
                      <option value={0}>No</option>
                    </select>
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Subtract Stock</label>
              {(() => {
                const err = getError("subtract");
                return (
                  <>
                    <select
                      className={"oc-select" + (err ? " oc-input-error" : "")}
                      value={form.subtract}
                      onChange={(e) =>
                        setField("subtract", Number(e.target.value))
                      }
                    >
                      <option value={1}>Yes</option>
                      <option value={0}>No</option>
                    </select>
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Is Flagged</label>
              {(() => {
                const err = getError("is_flagged");
                return (
                  <>
                    <select
                      className={"oc-select" + (err ? " oc-input-error" : "")}
                      value={form.is_flagged}
                      onChange={(e) =>
                        setField("is_flagged", Number(e.target.value))
                      }
                    >
                      <option value={0}>No</option>
                      <option value={1}>Yes</option>
                    </select>
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Is Sewing Flagged</label>
              {(() => {
                const err = getError("is_sewing_flagged");
                return (
                  <>
                    <select
                      className={"oc-select" + (err ? " oc-input-error" : "")}
                      value={form.is_sewing_flagged}
                      onChange={(e) =>
                        setField("is_sewing_flagged", Number(e.target.value))
                      }
                    >
                      <option value={0}>No</option>
                      <option value={1}>Yes</option>
                    </select>
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Weight</label>
              {(() => {
                const err = getError("weight");
                return (
                  <>
                    <input
                      type="number"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.weight}
                      onChange={(e) =>
                        setField("weight", Number(e.target.value || 0))
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Length</label>
              {(() => {
                const err = getError("length");
                return (
                  <>
                    <input
                      type="number"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.length}
                      onChange={(e) =>
                        setField("length", Number(e.target.value || 0))
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Width</label>
              {(() => {
                const err = getError("width");
                return (
                  <>
                    <input
                      type="number"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.width}
                      onChange={(e) =>
                        setField("width", Number(e.target.value || 0))
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Height</label>
              {(() => {
                const err = getError("height");
                return (
                  <>
                    <input
                      type="number"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.height}
                      onChange={(e) =>
                        setField("height", Number(e.target.value || 0))
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Date Available</label>
              {(() => {
                const err = getError("date_available");
                return (
                  <>
                    <input
                      type="date"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.date_available || ""}
                      onChange={(e) =>
                        setField("date_available", e.target.value)
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Location</label>
              {(() => {
                const err = getError("location");
                return (
                  <>
                    <input
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.location}
                      onChange={(e) => setField("location", e.target.value)}
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Pieces</label>
              {(() => {
                const err = getError("pieces");
                return (
                  <>
                    <input
                      type="number"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.pieces}
                      onChange={(e) =>
                        setField("pieces", Number(e.target.value || 0))
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Item Count</label>
              {(() => {
                const err = getError("item_count");
                return (
                  <>
                    <input
                      type="number"
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.item_count}
                      onChange={(e) =>
                        setField("item_count", Number(e.target.value || 0))
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field">
              <label>Delivery Within</label>
              {(() => {
                const err = getError("delivery_withen");
                return (
                  <>
                    <input
                      className={"oc-input" + (err ? " oc-input-error" : "")}
                      value={form.delivery_withen}
                      onChange={(e) =>
                        setField("delivery_withen", e.target.value)
                      }
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>

            <div className="oc-field" style={{ gridColumn: "1 / span 3" }}>
              <label>Categories</label>
              {(() => {
                const err = getError("category_ids");
                return (
                  <>
                    <CategoryMultiSelect
                      value={form.category_ids}
                      onChange={(ids) => setField("category_ids", ids)}
                    />
                    {err && <div className="oc-field-error">{err}</div>}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {tab === "special" && form.id && (
        <div className="oc-tab-pane">
          <ProductSpecialsTab productId={form.id} />
        </div>
      )}

      <div
        className="oc-actions"
        style={{ marginTop: 16, display: "flex", gap: 8 }}
      >
        <button className="oc-btn" type="submit">
          Save
        </button>
        <button className="oc-btn ghost" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

